import React, { Suspense, lazy, useEffect, useMemo } from 'react';
import { Shimmer, Stack } from '@fluentui/react';
import JitsiWrapper from './JitsiWrapper';
import PersonalMeetingCorner from 'features/Rooms/PersonalMeetingCorner/PersonalMeetingCorner';
import { IRenderJitsiOrWidgetsProps } from '../types';
import { useIsBottomImageExpanded, useIsUserAloneInOwnPersonalRoom, useTypeOfPersonalRoomTable } from 'features/Rooms/PersonalMeetingCorner/states/hooks';
import { useLocalProfile } from 'shared-state/identity/hooks';
import DraggableJitsiForPersonalDesk from './DraggableJitsiForPersonalDesk';
import { useWindowWidth } from 'shared-state/display-options/hooks';
import { useCurrentOrgId } from 'shared-state/location/hook';
import { useOrganizationBranding } from 'features/Header/hooks';
import AegeanSeaImg from 'media/img/dashboard-bg-aegean-sea.jpg';
import EuropeanSquareImg from 'media/img/dashboard-bg-european-square.jpg';
import TimberTownImg from 'media/img/dashboard-bg-timber-town.jpg';
import RollingHillsImg from 'media/img/dashboard-bg-rolling-hills.jpg';
import { ErrorBoundary } from 'components/ErrorBoundary/ErrorBoundary';

const PersonalDashboard = lazy(() => import('features/Rooms/PersonalDashboard/PersonalDashboard'));

const DASHBOARD_BACKGROUNDS = [AegeanSeaImg, EuropeanSquareImg, TimberTownImg, RollingHillsImg];

const DashboardFallback: React.FC = () => (
    <div
        style={{
            width: '100%',
            height: '100%',
            display: 'grid',
            gridTemplateColumns: 'repeat(12, 1fr)',
            gridTemplateRows: 'repeat(12, 1fr)',
            gap: 16,
            padding: 16,
            boxSizing: 'border-box'
        }}
    >
        <Stack
            style={{
                gridColumn: '1 / span 8',
                gridRow: '1 / span 8',
                background: 'white',
                borderRadius: 4,
                padding: 16
            }}
            tokens={{ childrenGap: 10 }}
        >
            <Shimmer width="35%" />
            <Shimmer width="60%" />
            <Shimmer />
            <Shimmer />
            <Shimmer width="82%" />
            <Shimmer width="48%" />
        </Stack>
        <Stack
            style={{
                gridColumn: '9 / span 4',
                gridRow: '1 / span 4',
                background: 'white',
                borderRadius: 4,
                padding: 16
            }}
            tokens={{ childrenGap: 8 }}
        >
            <Shimmer width="55%" />
            <Shimmer />
            <Shimmer width="75%" />
        </Stack>
        <Stack
            style={{
                gridColumn: '9 / span 4',
                gridRow: '5 / span 4',
                background: 'white',
                borderRadius: 4,
                padding: 16
            }}
            tokens={{ childrenGap: 8 }}
        >
            <Shimmer width="42%" />
            <Shimmer />
            <Shimmer />
        </Stack>
    </div>
);

const RenderJitsiOrWidgets: React.FC<IRenderJitsiOrWidgetsProps> = (props) => {
    const {
        shouldRenderJitsi,
        triggerJitsiRender,
        domain,
        roomName,
        jitsiConfig,
        interfaceConfig,
        jwt,
        displayName,
        userEmailAddress,
        addOnIsVisible,
        handleJitsiMeetExternalAPILoad
    } = props;

    const [isBottomImageExpanded] = useIsBottomImageExpanded();
    const localProfile = useLocalProfile();
    const [typeOfPersonalRoomTable, setTypeOfPersonalRoomTable] = useTypeOfPersonalRoomTable();
    const isUserAloneInOwnPersonalRoom = useIsUserAloneInOwnPersonalRoom();
    const { smallDevice, mediumDevice } = useWindowWidth();
    const isNotLaptop = smallDevice || mediumDevice;
    const isJitsiActive = shouldRenderJitsi || triggerJitsiRender;
    const [orgId] = useCurrentOrgId();
    const [branding] = useOrganizationBranding(orgId);

    const bgIndex = branding?.dashboard_background_image ?? 0;
    const dashboardBgSrc = DASHBOARD_BACKGROUNDS[bgIndex] ?? DASHBOARD_BACKGROUNDS[0];
    const isOwnPersonalRoom = useMemo(() => localProfile && roomName.includes('personal-') && roomName.includes(localProfile.uid), [localProfile, roomName]);
    const isPersonalRoom = useMemo(() => roomName.includes('personal-'), [roomName]);

    useEffect(() => {
        if (isJitsiActive && (isOwnPersonalRoom || isPersonalRoom) && !isNotLaptop && !isUserAloneInOwnPersonalRoom) {
            setTypeOfPersonalRoomTable('meeting-table');
        }
    }, [isJitsiActive, isOwnPersonalRoom, isPersonalRoom, setTypeOfPersonalRoomTable, isNotLaptop, isUserAloneInOwnPersonalRoom]);

    useEffect(() => {
        if (isUserAloneInOwnPersonalRoom) {
            setTypeOfPersonalRoomTable('desk');
        }
    }, [isUserAloneInOwnPersonalRoom, setTypeOfPersonalRoomTable]);

    const renderJitsi = (style: React.CSSProperties) => (
        <div style={style}>
            <JitsiWrapper
                domain={domain}
                roomName={roomName}
                configOverwrite={jitsiConfig}
                interfaceConfigOverwrite={interfaceConfig}
                jwt={jwt}
                displayName={displayName}
                userEmailAddress={userEmailAddress}
                addOnIsVisible={addOnIsVisible}
                handleJitsiMeetExternalAPILoad={handleJitsiMeetExternalAPILoad}
            />
        </div>
    );

    // Where the call sits in each personal-room layout. undefined = the floating, draggable desk tile.
    const personalRoomCallDockedStyle: React.CSSProperties | undefined =
        typeOfPersonalRoomTable === 'meeting-table'
            ? {
                  position: 'absolute',
                  top: isBottomImageExpanded ? '3%' : '1%',
                  left: isBottomImageExpanded ? '12.5%' : '5%',
                  right: isBottomImageExpanded ? '12.5%' : '5%',
                  bottom: isBottomImageExpanded ? '37%' : '10%',
                  zIndex: 10
              }
            : isNotLaptop
            ? { position: 'relative', width: '100%', height: '100%' }
            : undefined;

    // One call element for every personal-room layout: always the first child of the layout's root,
    // always this same component, with only its style changing. Switching desk <-> meeting table, or
    // crossing the laptop breakpoint, used to render the call inside a different element tree, so
    // React unmounted JitsiWrapper and mounted a new one - and the SDK builds its iframe on mount,
    // so every switch was a full rejoin (black flash, renegotiation, camera re-acquired). Moving
    // the iframe elsewhere in the DOM would reload it just the same, which is why the layouts are
    // built around a call that stays put rather than the call being moved between them.
    const personalRoomCall = isJitsiActive && (
        <DraggableJitsiForPersonalDesk jitsiSize={{ width: 320, height: 280 }} dockedStyle={personalRoomCallDockedStyle}>
            {renderJitsi({ width: '100%', height: '100%' })}
        </DraggableJitsiForPersonalDesk>
    );

    // Early return: Personal Room Logic
    if (isOwnPersonalRoom || isPersonalRoom) {
        if (typeOfPersonalRoomTable === 'meeting-table') {
            return (
                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                    {personalRoomCall}
                    <PersonalMeetingCorner />
                </div>
            );
        }

        if (typeOfPersonalRoomTable === 'desk') {
            return (
                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                    {personalRoomCall}
                    <img
                        src={dashboardBgSrc}
                        alt="Dashboard background"
                        style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: isNotLaptop ? 'fill' : 'cover',
                            objectPosition: 'center center',
                            zIndex: 0
                        }}
                    />
                    <div style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
                        <ErrorBoundary fallback={<DashboardFallback />}>
                            <Suspense fallback={<DashboardFallback />}>
                                <PersonalDashboard />
                            </Suspense>
                        </ErrorBoundary>
                    </div>
                </div>
            );
        }
    }

    // Early return: General Room Jitsi View
    if (isJitsiActive) {
        return <div style={{ position: 'relative', width: '100%', height: '100%' }}>{renderJitsi({ width: '100%', height: '100%' })}</div>;
    }

    // Default fallback
    return null;
};

export default RenderJitsiOrWidgets;
